// S4 · column-mapping editor (Module 02 §4 S4, §3.3 step 3).
//
// The parse worker auto-proposes a mapping by header match; this is where a
// human confirms it. It shows every column the sheet actually had (from the
// parsed rows, in sheet order) with its first real value, what it maps to, and
// whether that match was proposed or changed by hand.
//
// Two guards before the file can be checked, both mirrors of server behaviour:
//  * required targets must all be mapped (422 MAPPING_INCOMPLETE otherwise);
//  * two columns may not claim the same target — server-side the second would
//    silently win, which is exactly the kind of quiet data loss this wizard
//    exists to prevent.
//
// A lane whose owning module has not shipped has no published field catalog
// (§3.3), so its targets are typed free-text rather than invented here.

import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSetImportMapping } from "../../hooks/onboarding-queries";
import { Button } from "../../../../components/button";
import { Select } from "../../../../components/select";
import { EmptyState } from "../../../../components/states";
import {
  REQUIRED_TARGET_FIELDS,
  cleanMapping,
  hasFieldCatalog,
  mappingIssues,
  sampleValue,
  sourceHeaders,
  targetOptionsFor,
  type ImportJob,
  type ImportRow,
} from "./import-model";
import { ImportProblemNotice } from "./import-problem-notice";
import { useImportLabels } from "./labels";
import { recallMapping, rememberMapping } from "./mapping-memory";

export interface MappingEditorProps {
  job: ImportJob;
  /** A sample page of parsed rows — supplies the sheet's real header order. */
  rows: readonly ImportRow[];
  /** The caller may not run this lane, or the job is past mapping. */
  readOnly: boolean;
  /** Run validation (owned by the screen: it also drives the status change). */
  onValidate: () => void;
  validating: boolean;
  /** `POST /validate` only accepts a job in `mapping` (§3.3). Once it has been
   * checked, re-checking goes through "check again", which re-saves the mapping
   * first — so the button is disabled here rather than earning a 409. */
  canValidate: boolean;
}

export function MappingEditor({
  job,
  rows,
  readOnly,
  onValidate,
  validating,
  canValidate,
}: MappingEditorProps) {
  const { t } = useTranslation();
  const labels = useImportLabels();
  const save = useSetImportMapping(job.id);

  const serverMapping = job.column_mapping;
  const headers = useMemo(() => sourceHeaders(serverMapping, rows), [serverMapping, rows]);
  const serverKey = JSON.stringify(serverMapping);

  const [draft, setDraft] = useState<Record<string, string>>(() => seed(headers, serverMapping));
  // Re-seed whenever the server's mapping changes underneath us (a save, a
  // refetch, another admin) so the editor never shows a stale proposal.
  useEffect(() => {
    setDraft(seed(headers, JSON.parse(serverKey) as Record<string, string>));
  }, [headers, serverKey]);

  // The mapping this browser last CONFIRMED for a sheet with these exact
  // headers. Offered, never auto-applied (see mapping-memory.ts).
  const remembered = useMemo(() => recallMapping(job.kind, headers), [job.kind, headers]);
  const rememberedDiffers =
    remembered !== null &&
    JSON.stringify(cleanMapping(remembered)) !== JSON.stringify(cleanMapping(draft));

  const issues = useMemo(() => mappingIssues(job.kind, draft), [job.kind, draft]);
  const dirty = useMemo(
    () => JSON.stringify(cleanMapping(draft)) !== JSON.stringify(cleanMapping(serverMapping)),
    [draft, serverMapping],
  );
  const catalogued = hasFieldCatalog(job.kind);

  if (headers.length === 0) {
    return (
      <EmptyState
        title={t("onboarding.importDetail.mapping.noHeadersTitle")}
        body={t("onboarding.importDetail.mapping.noHeadersBody")}
      />
    );
  }

  const requiredFields = new Set(REQUIRED_TARGET_FIELDS[job.kind] ?? []);

  return (
    <section className="import-mapping" data-testid="mapping-editor">
      <header className="import-section-head">
        <h3>{t("onboarding.importDetail.mapping.title")}</h3>
        <p className="wizard-step-blurb">{t("onboarding.importDetail.mapping.blurb")}</p>
      </header>

      {save.error !== null ? (
        <ImportProblemNotice
          kind={job.kind}
          error={save.error}
          onRetry={() => {
            save.reset();
          }}
        />
      ) : null}

      {!readOnly && rememberedDiffers ? (
        <div className="import-mapping-recall" data-testid="mapping-recall">
          <p className="wizard-save-bar-note">{t("onboarding.importDetail.mapping.recallBody")}</p>
          <Button
            onClick={() => {
              setDraft(seed(headers, remembered));
            }}
          >
            {t("onboarding.importDetail.mapping.recallCta")}
          </Button>
        </div>
      ) : null}

      {issues.missingRequired.length > 0 ? (
        <div className="state state-error wizard-notice" role="alert" data-testid="mapping-missing">
          <p className="state-title">{t("onboarding.importDetail.mapping.missingTitle")}</p>
          <p className="state-detail">{t("onboarding.importDetail.mapping.missingBody")}</p>
          <ul className="wizard-notice-list">
            {issues.missingRequired.map((field) => (
              <li key={field}>{labels.fieldLabel(field)}</li>
            ))}
          </ul>
        </div>
      ) : null}

      {issues.duplicateTargets.length > 0 ? (
        <div
          className="state state-error wizard-notice"
          role="alert"
          data-testid="mapping-duplicate"
        >
          <p className="state-title">{t("onboarding.importDetail.mapping.duplicateTitle")}</p>
          <p className="state-detail">{t("onboarding.importDetail.mapping.duplicateBody")}</p>
          <ul className="wizard-notice-list">
            {issues.duplicateTargets.map((field) => (
              <li key={field}>{labels.fieldLabel(field)}</li>
            ))}
          </ul>
        </div>
      ) : null}

      <ul className="import-mapping-list">
        <li className="import-mapping-row import-mapping-head" aria-hidden="true">
          <span>{t("onboarding.importDetail.mapping.colSource")}</span>
          <span>{t("onboarding.importDetail.mapping.colSample")}</span>
          <span>{t("onboarding.importDetail.mapping.colTarget")}</span>
        </li>
        {headers.map((header) => {
          const value = draft[header] ?? "";
          const proposed = serverMapping[header] ?? "";
          const edited = value !== proposed;
          const controlId = `map-${slug(header)}`;
          return (
            <li className="import-mapping-row" key={header} data-testid="mapping-row">
              <span className="import-mapping-source">
                <label htmlFor={controlId}>{header}</label>
                {!edited && proposed.length > 0 ? (
                  <span className="badge badge-auto">
                    {t("onboarding.importDetail.mapping.auto")}
                  </span>
                ) : null}
                {edited ? (
                  <span className="badge badge-edited">
                    {t("onboarding.importDetail.mapping.edited")}
                  </span>
                ) : null}
                {requiredFields.has(value) ? (
                  <span className="badge badge-required">
                    {t("onboarding.importDetail.mapping.required")}
                  </span>
                ) : null}
              </span>
              <span className="import-mapping-sample" title={sampleValue(header, rows)}>
                {sampleValue(header, rows)}
              </span>
              <span className="import-mapping-target">
                {catalogued ? (
                  <Select
                    id={controlId}
                    value={value}
                    disabled={readOnly}
                    onChange={(e) => {
                      setDraft((prev) => ({ ...prev, [header]: e.target.value }));
                    }}
                  >
                    <option value="">{t("onboarding.importShared.fieldUnmapped")}</option>
                    {targetOptionsFor(job.kind, header).map((field) => (
                      <option key={field} value={field}>
                        {labels.fieldLabel(field)}
                      </option>
                    ))}
                  </Select>
                ) : (
                  <input
                    id={controlId}
                    className="ds-input"
                    type="text"
                    value={value}
                    disabled={readOnly}
                    placeholder={t("onboarding.importShared.fieldCustom")}
                    onChange={(e) => {
                      setDraft((prev) => ({ ...prev, [header]: e.target.value }));
                    }}
                  />
                )}
              </span>
            </li>
          );
        })}
      </ul>

      {!catalogued ? (
        <p className="wizard-save-bar-note">{t("onboarding.importShared.fieldCustomHint")}</p>
      ) : null}

      <div className="import-actions">
        <Button
          busy={save.isPending}
          disabled={readOnly || !dirty}
          onClick={() => {
            const confirmed = cleanMapping(draft);
            save.mutate(
              { column_mapping: confirmed },
              {
                // Only a mapping the SERVER accepted is worth offering back.
                onSuccess: () => {
                  rememberMapping(job.kind, headers, confirmed);
                },
              },
            );
          }}
        >
          {save.isPending
            ? t("onboarding.importDetail.mapping.saving")
            : t("onboarding.importDetail.mapping.save")}
        </Button>
        <Button
          variant="primary"
          disabled={readOnly || dirty || !issues.ok || validating || !canValidate}
          onClick={onValidate}
        >
          {validating
            ? t("onboarding.importDetail.mapping.validating")
            : t("onboarding.importDetail.mapping.validate")}
        </Button>
        {/* §4 S4's "Ask agent to map" renders once module 14's tool registry is
            in the deployment. Until then nothing stands in its place: a dead
            button is a build-status line in another shape (D-425). */}
        {save.isSuccess && !dirty ? (
          <span className="wizard-save-bar-note" role="status">
            {t("onboarding.importDetail.mapping.saved")}
          </span>
        ) : null}
      </div>
    </section>
  );
}

function seed(headers: readonly string[], mapping: Record<string, string>): Record<string, string> {
  const next: Record<string, string> = {};
  for (const header of headers) next[header] = mapping[header] ?? "";
  return next;
}

/** A DOM-id-safe token for a source header (headers can be anything at all). */
function slug(header: string): string {
  return header.replace(/[^a-zA-Z0-9_-]/g, "_");
}
